import React from 'react';
import { Trash2, Copy, RotateCw } from 'lucide-react';
import { LabelElement, LabelPlaceholder, TableSchema, BarcodeFormat, TextAlign } from '../../../../../types/labelDesign';

interface PropertyPanelProps {
  element: LabelElement | null;
  placeholders: LabelPlaceholder[];
  tableSchemas?: TableSchema[];
  onChange: (element: LabelElement) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

const inputCls =
  'w-full px-2 py-1 border border-[var(--color-border)] rounded bg-[var(--color-surface)] text-[var(--color-text-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]';
const labelCls = 'block text-xs font-medium text-[var(--color-text-secondary)] mb-1';

export const PropertyPanel: React.FC<PropertyPanelProps> = ({ element, placeholders, tableSchemas = [], onChange, onDelete, onDuplicate }) => {
  if (!element) {
    return (
      <div className="p-3 border-l border-[var(--color-border)] bg-[var(--color-surface)] w-72 text-sm text-[var(--color-text-secondary)]">
        Velg et element i canvas for å redigere egenskaper.
      </div>
    );
  }

  const update = (patch: Partial<LabelElement>) => onChange({ ...element, ...patch });

  const insertPlaceholder = (field: 'text' | 'value', key: string) => {
    const current = element[field] || '';
    update({ [field]: `${current}{{${key}}}` });
  };

  return (
    <div className="p-3 border-l border-[var(--color-border)] bg-[var(--color-surface)] w-72 overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {element.type}
        </h3>
        <div className="flex gap-1">
          <button
            onClick={onDuplicate}
            className="p-1 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)]"
            title="Dupliser"
          >
            <Copy size={14} />
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded hover:bg-red-100 text-red-600"
            title="Slett"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Position + size */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <label className={labelCls}>X (mm)</label>
          <input type="number" step="0.1" className={inputCls} value={round(element.x)}
            onChange={(e) => update({ x: Number(e.target.value) })} />
        </div>
        <div>
          <label className={labelCls}>Y (mm)</label>
          <input type="number" step="0.1" className={inputCls} value={round(element.y)}
            onChange={(e) => update({ y: Number(e.target.value) })} />
        </div>
        <div>
          <label className={labelCls}>Bredde (mm)</label>
          <input type="number" step="0.1" className={inputCls} value={round(element.width)}
            onChange={(e) => update({ width: Number(e.target.value) })} />
        </div>
        <div>
          <label className={labelCls}>Høyde (mm)</label>
          <input type="number" step="0.1" className={inputCls} value={round(element.height)}
            onChange={(e) => update({ height: Number(e.target.value) })} />
        </div>
      </div>

      {/* Type-specific */}
      {element.type === 'Text' && (
        <>
          <div className="mb-2">
            <label className={labelCls}>Tekst</label>
            <textarea
              className={`${inputCls} min-h-[60px]`}
              value={element.text || ''}
              onChange={(e) => update({ text: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className={labelCls}>Fontstørrelse (pt)</label>
              <input type="number" className={inputCls} value={element.fontSize || 10}
                onChange={(e) => update({ fontSize: Number(e.target.value) })} />
            </div>
            <div>
              <label className={labelCls}>Justering</label>
              <select className={inputCls} value={element.textAlign || 'Left'}
                onChange={(e) => update({ textAlign: e.target.value as TextAlign })}>
                <option value="Left">Venstre</option>
                <option value="Center">Senter</option>
                <option value="Right">Høyre</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 mb-2">
            <label className="flex items-center gap-1 text-sm text-[var(--color-text-primary)]">
              <input type="checkbox" checked={!!element.bold}
                onChange={(e) => update({ bold: e.target.checked })} /> Fet
            </label>
            <label className="flex items-center gap-1 text-sm text-[var(--color-text-primary)]">
              <input type="checkbox" checked={!!element.italic}
                onChange={(e) => update({ italic: e.target.checked })} /> Kursiv
            </label>
          </div>

          <div className="mb-2">
            <label className={labelCls}>Tekstfarge</label>
            <input type="color" className={`${inputCls} h-8 p-1`} value={element.color || '#000000'}
              onChange={(e) => update({ color: e.target.value })} />
          </div>

          <PlaceholderInserter placeholders={placeholders} tableSchemas={tableSchemas} onInsert={(key) => insertPlaceholder('text', key)} />
        </>
      )}

      {(element.type === 'Barcode' || element.type === 'QrCode') && (
        <>
          <div className="mb-2">
            <label className={labelCls}>Verdi</label>
            <input className={inputCls} value={element.value || ''}
              onChange={(e) => update({ value: e.target.value })} />
          </div>

          {element.type === 'Barcode' && (
            <>
              <div className="mb-2">
                <label className={labelCls}>Strekkode-format</label>
                <select className={inputCls} value={element.barcodeFormat || 'Code128'}
                  onChange={(e) => update({ barcodeFormat: e.target.value as BarcodeFormat })}>
                  <option value="Code128">Code 128</option>
                  <option value="Code39">Code 39</option>
                  <option value="Ean13">EAN-13</option>
                  <option value="Ean8">EAN-8</option>
                  <option value="Gs1128">GS1-128</option>
                </select>
              </div>

              <label className="flex items-center gap-1 text-sm text-[var(--color-text-primary)] mb-2">
                <input type="checkbox" checked={element.showText !== false}
                  onChange={(e) => update({ showText: e.target.checked })} /> Vis lesbar tekst
              </label>

              {element.showText !== false && (
                <div className="mb-2">
                  <label className={labelCls}>Justering (lesbar tekst)</label>
                  <select className={inputCls} value={element.textAlign || 'Center'}
                    onChange={(e) => update({ textAlign: e.target.value as TextAlign })}>
                    <option value="Left">Venstre</option>
                    <option value="Center">Senter</option>
                    <option value="Right">Høyre</option>
                  </select>
                </div>
              )}
            </>
          )}

          <PlaceholderInserter placeholders={placeholders} tableSchemas={tableSchemas} onInsert={(key) => insertPlaceholder('value', key)} />
        </>
      )}

      {element.type === 'Image' && (
        <ImageSourceEditor element={element} update={update} labelCls={labelCls} inputCls={inputCls} />
      )}

      {(element.type === 'Rectangle' || element.type === 'Line') && (
        <>
          {element.type === 'Rectangle' && (
            <div className="mb-2">
              <label className={labelCls}>Fyllfarge</label>
              <input type="color" className={`${inputCls} h-8 p-1`} value={element.fillColor || '#ffffff'}
                onChange={(e) => update({ fillColor: e.target.value })} />
            </div>
          )}
          <div className="mb-2">
            <label className={labelCls}>{element.type === 'Line' ? 'Linjefarge' : 'Kantfarge'}</label>
            <input type="color" className={`${inputCls} h-8 p-1`} value={element.borderColor || '#000000'}
              onChange={(e) => update({ borderColor: e.target.value })} />
          </div>
          <div className="mb-2">
            <label className={labelCls}>{element.type === 'Line' ? 'Linjetykkelse (mm)' : 'Kanttykkelse (mm)'}</label>
            <input type="number" step="0.1" className={inputCls} value={element.borderWidth || 0.3}
              onChange={(e) => update({ borderWidth: Number(e.target.value) })} />
          </div>
          {element.type === 'Line' && (
            <div className="mb-2">
              <label className={labelCls}>Retning</label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--color-text-secondary)]">
                  {element.height > element.width ? 'Vertikal' : 'Horisontal'}
                </span>
                <button
                  type="button"
                  onClick={() => update({ width: element.height, height: element.width })}
                  className="flex items-center gap-1 px-2 py-1 text-xs border border-[var(--color-border)] rounded hover:bg-gray-50"
                  title="Bytt mellom horisontal og vertikal"
                >
                  <RotateCw size={12} />
                  Snu
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const PlaceholderInserter: React.FC<{
  placeholders: LabelPlaceholder[];
  tableSchemas: TableSchema[];
  onInsert: (key: string) => void;
}> = ({ placeholders, tableSchemas, onInsert }) => {
  // Grupper enkle plassholdere etter kategori, behold rekkefølgen fra backend
  const groups = React.useMemo(() => {
    const map = new Map<string, LabelPlaceholder[]>();
    placeholders.forEach((p) => {
      const existing = map.get(p.category) || [];
      existing.push(p);
      map.set(p.category, existing);
    });
    return Array.from(map.entries());
  }, [placeholders]);

  return (
    <div className="mt-3 pt-3 border-t border-[var(--color-border)] space-y-3">
      <div>
        <label className={labelCls}>Sett inn plassholder (system)</label>
        <select
          className={inputCls}
          value=""
          onChange={(e) => {
            if (e.target.value) {
              onInsert(e.target.value);
              e.target.value = '';
            }
          }}
          disabled={placeholders.length === 0}
        >
          <option value="">{placeholders.length === 0 ? 'Laster…' : 'Velg…'}</option>
          {groups.map(([category, items]) => (
            <optgroup key={category} label={category}>
              {items.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label} ({`{{${p.key}}}`})
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div>
        <label className={labelCls}>Sett inn fra tabell-kolonne</label>
        <select
          className={inputCls}
          value=""
          onChange={(e) => {
            if (e.target.value) {
              onInsert(e.target.value);
              e.target.value = '';
            }
          }}
          disabled={tableSchemas.length === 0}
        >
          <option value="">
            {tableSchemas.length === 0 ? 'Ingen tabeller tilgjengelig' : 'Velg tabell + kolonne…'}
          </option>
          {tableSchemas.map((schema) => (
            <optgroup key={schema.tableName} label={schema.displayName}>
              {schema.columns.map((col) => {
                const key = `${schema.tableName}.${col.name}`;
                return (
                  <option key={key} value={key}>
                    {col.name} ({col.sqlType})
                  </option>
                );
              })}
            </optgroup>
          ))}
        </select>
        <p className="text-xs text-[var(--color-text-secondary)] mt-1">
          Nøkkel: {'{{Tabell.Kolonne}}'} — verdien hentes ved utskrift.
        </p>
      </div>
    </div>
  );
};

/**
 * Editor for Image-elementer. Tre kilder: tenant-logo (referanse), opplastet bilde
 * (data-URI lagret direkte i layouten), eller fjernet (tomt).
 *
 * Opplasting konverteres til base64 data-URI via FileReader. Maks-grense på 500 KB
 * for å unngå at en enkelt rapport sveller LayoutJson urimelig — base64-økningen
 * er ~33%, så 500 KB binær fil → ~670 KB i Reports.LayoutJson.
 */
interface ImageSourceEditorProps {
  element: LabelElement;
  update: (patch: Partial<LabelElement>) => void;
  labelCls: string;
  inputCls: string;
}

const MAX_IMAGE_BYTES = 500 * 1024; // 500 KB

const ImageSourceEditor: React.FC<ImageSourceEditorProps> = ({ element, update, labelCls, inputCls }) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [error, setError] = React.useState<string | null>(null);

  const hasUploadedImage = !!element.imageSource && element.imageSource.startsWith('data:');

  // Brukerens valgte modus holdes som lokal state slik at "Opplastet bilde" forblir
  // valgt selv om bildet ikke er lastet opp ennå (eller hvis brukeren avbrøt file-picker).
  // Initialiseres fra element.imageSource: data-URI → upload, ellers logo.
  const [mode, setMode] = React.useState<'logo' | 'upload'>(
    hasUploadedImage ? 'upload' : 'logo'
  );

  // Reset når brukeren bytter til et annet element i canvas
  React.useEffect(() => {
    setMode(hasUploadedImage ? 'upload' : 'logo');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.id]);

  const handleModeChange = (next: 'logo' | 'upload') => {
    setError(null);
    setMode(next);
    if (next === 'logo') {
      update({ imageSource: 'logo' });
    } else if (!hasUploadedImage) {
      // Ikke endre imageSource ennå — vent til bruker laster opp.
      // Åpne file-picker som en bekvemmelighet.
      fileInputRef.current?.click();
    }
  };

  const handleFile = (file: File) => {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Filen er ikke et bilde. Velg PNG, JPG eller WebP.');
      return;
    }
    if (file.type === 'image/svg+xml') {
      setError('SVG støttes ikke i utskrift (kun raster-bilder). Konverter til PNG først.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError(`Bildet er for stort (${Math.round(file.size / 1024)} KB). Maks 500 KB. Komprimer eller skaler ned.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUri = e.target?.result;
      if (typeof dataUri !== 'string') return;

      // Last bildet i memory for å lese naturalWidth/Height og justere
      // element-høyden så proporsjonen matcher bildet (bredde beholdes).
      const probe = new window.Image();
      probe.onload = () => {
        const ratio = probe.naturalWidth / probe.naturalHeight;
        if (ratio > 0 && isFinite(ratio)) {
          const adjustedHeight = Math.max(2, Math.round((element.width / ratio) * 10) / 10);
          update({ imageSource: dataUri, height: adjustedHeight });
        } else {
          update({ imageSource: dataUri });
        }
      };
      probe.onerror = () => update({ imageSource: dataUri });
      probe.src = dataUri;
    };
    reader.onerror = () => setError('Kunne ikke lese filen.');
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    setError(null);
    // Fjern selve bildet, men behold upload-modus så brukeren kan velge et nytt
    update({ imageSource: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sizeKb = hasUploadedImage && element.imageSource
    ? Math.round((element.imageSource.length * 0.75) / 1024) // base64 → ~bytes
    : 0;

  return (
    <div className="mb-2 space-y-2">
      <div>
        <label className={labelCls}>Bilde-kilde</label>
        <select
          className={inputCls}
          value={mode}
          onChange={(e) => handleModeChange(e.target.value as 'logo' | 'upload')}
        >
          <option value="logo">Tenant-logo</option>
          <option value="upload">Opplastet bilde</option>
        </select>
        {mode === 'logo' && (
          <p className="text-xs text-amber-600 mt-1">
            Tenant-logo er en plassholder for firmaets logo, men koblingen til lagret logo er ikke aktiv ennå. Bruk &quot;Opplastet bilde&quot; for å få et synlig bilde på rapporten.
          </p>
        )}
      </div>

      {mode === 'upload' && (
        <>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
              className="block w-full text-xs text-[var(--color-text-secondary)] file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {hasUploadedImage && (
            <div className="border border-[var(--color-border)] rounded p-2 bg-[var(--color-background)]">
              <div className="flex items-start gap-2">
                <img
                  src={element.imageSource}
                  alt="Forhåndsvisning"
                  className="max-w-[80px] max-h-[60px] object-contain border border-[var(--color-border)] bg-white"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[var(--color-text-secondary)]">~{sizeKb} KB lagret i layout</p>
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="mt-1 text-xs text-red-600 hover:underline"
                  >
                    Fjern bilde
                  </button>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-[var(--color-text-secondary)]">
            Støtter PNG, JPG, WebP. Maks 500 KB. Element-høyde justeres automatisk til bildets proporsjon.
          </p>
        </>
      )}

      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}
    </div>
  );
};

const round = (n: number) => Math.round(n * 10) / 10;
